/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import {
  calendarColor,
  BRAND_COLOR,
  VISITS_COLOR,
} from '@features/agenda/utils/calendar-display';
import { isVisitEvent } from '@features/agenda/utils/visit-event';

const DEFAULT_COLOR = BRAND_COLOR;

/**
 * Resolves the display color for an event from its owning calendar.
 * @param event - The expanded event.
 * @param calendars - The available calendars to match against.
 * @returns The resolved color, or the brand default when unmatched.
 */
export const eventColor = (
  event: ExpandedEvent,
  calendars: Calendar[]
): string => {
  // A colour stamped on the occurrence wins: it records why the event is on
  // screen (a member's row) which its calendar cannot express — an event Lucas
  // organizes on the institutional calendar would otherwise be painted the
  // agency wine while sitting under his blue swatch.
  if (event.display_color) return event.display_color;
  // Visit projections carry no calendar_id, so they used to land on the brand
  // wine while the Visitas row in the sidebar is brown — the layer's colour is
  // the one the user toggles them by, so it is the one they are painted with.
  if (isVisitEvent(event)) return VISITS_COLOR;
  const calendar = calendars.find((c) => c.calendar_id === event.calendar_id);
  if (!calendar) return DEFAULT_COLOR;
  return calendarColor(calendar);
};

/**
 * Computes the WCAG relative luminance of a `#rrggbb`/`#rgb` hex color.
 * @param color - The hex color string.
 * @returns Luminance in [0, 1], or null for non-hex inputs (e.g. CSS vars).
 */
export const hexLuminance = (color: string): number | null => {
  const hex = color.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const channel = (start: number): number => {
    const v = parseInt(full.slice(start, start + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
};

/**
 * Parses a `#rrggbb` / `#rgb` hex color into linear [0,1] RGB channels.
 * @param color - The hex color string.
 * @returns The linear RGB channels, or null for non-hex inputs.
 */
const hexToLinearRgb = (
  color: string
): { r: number; g: number; b: number } | null => {
  const hex = color.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const toLinear = (start: number): number => {
    const v = parseInt(full.slice(start, start + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return { r: toLinear(0), g: toLinear(2), b: toLinear(4) };
};

/**
 * Computes the effective luminance of a chip drawn as `color` at 25% alpha
 * over a white (#ffffff) background — mirrors the `${color}40` chip fill.
 * @param color - The chip accent hex color.
 * @returns The composited luminance, or null for non-hex inputs.
 */
const effectiveChipLuminance = (color: string): number | null => {
  const fg = hexToLinearRgb(color);
  if (!fg) return null;
  const alpha = 0.25;
  const composited = {
    r: fg.r * alpha + 1 * (1 - alpha),
    g: fg.g * alpha + 1 * (1 - alpha),
    b: fg.b * alpha + 1 * (1 - alpha),
  };
  return 0.2126 * composited.r + 0.7152 * composited.g + 0.0722 * composited.b;
};

/**
 * Picks a text color with WCAG AA contrast over a translucent chip background
 * rendered as `color` at ~25% alpha on white. Evaluates the composited
 * background luminance so dark accents on a light chip still get dark text.
 * @param color - The chip accent hex color.
 * @returns A CSS color value for readable chip text.
 */
export const readableEventText = (color: string): string => {
  const lum = effectiveChipLuminance(color);
  if (lum === null) return 'var(--text-primary)';
  return lum > 0.45 ? 'var(--text-primary)' : color;
};
